import { i18n } from '@/plugins/i18n'
import JSEncrypt from 'jsencrypt';
import CryptoJS from 'crypto-js';

function deIdentifyPatient(patient, identifyingPanels) {
    const fields = {
        age: 'age',
        ageInDays: 'age',
        gender: 'gender',
        name: 'patient_name',
        patientId: 'patient_id',
        phoneNumber: 'patient_phone',
        birthDate: 'age'
    };

    return Object.fromEntries(
        Object.entries(patient).map(([key, value]) => [
            key,
            identifyingPanels.includes(fields[key]) ? undefined : value
        ])
    );
}

export default {

    async reset({ commit }) {
        commit('set', {
            uploaded: false,
            queued: true
        })
        return
    },

    async resetQueue({ commit }) {
        commit('set', {
            queueStartedAt: new Date().toISOString(),
            track: 0,
            upload: 0,
        })
        return
    },

    async setLastUploadDate({ commit }) {
        commit('set', {
            lastUploadDate: new Date().toISOString()
        })
        return
    },

    async incrementUploadCounter({ commit, state }) {
        let count = state.upload || 0
        commit('set', {
            upload: count + 1
        })
        return
    },

    async incrementTrackCounter({ commit, state }) {
        let count = state.track || 0
        commit('set', {
            track: count + 1
        })
        return
    },

    async incrementSequenceCounter({ commit, state }, payload) {
        commit('set', {
            globalSequence: state.globalSequence + 1
        })
        let count = state.sequences[payload] || 1
        state.sequences[payload] = count + 1
        return
    },

    async checkIfServerIsAvailable({ commit }) {
        let results = false
        try {

            const options = {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            }

            let response = await fetch('api/v3/device/ready')
            results = response.ok

        }
        catch (e) {
            console.log(e)
        }

        commit('set', { serverIsAvailable: results })
        return results
    },

    async genericEvent({ rootState, state }, payload) {
        try {
            let dataOptions = {
                globalSequence: state.globalSequence,
                encountersInQueue: rootState.recordsInQueue,
                encounterId: rootState.v4.session.uuid
            }

            let data = payload.data || {}

            if (payload.additions) {
                data = payload.additions.reduce((acc, cur) => {
                    acc[cur] = dataOptions[cur]
                    return acc
                }, data)
            }

            let content = {
                name: payload.name,
                device: {
                    engagement: rootState.settings.code,
                    ...rootState.v4.registration
                },
                data: data
            }

            const options = {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(content)
            }
            await fetch('api/v3/device/event', options)
        }
        catch (e) {}

    },

    async send({ rootState, getters, dispatch, commit, rootGetters }) {
        let results = { ok: false }
        try {
            if (getters['ready']) {

                await dispatch('incrementTrackCounter')
                commit('set', { uploaded: true })

                if (!rootGetters['v4/settings/get']('cdn')) {
                    
                    let body = await dispatch('prep', { deIdentify: true });

                    await dispatch('incrementSequenceCounter', body.track.name)

                    if (rootState.settings.webhook_url != null)
                    {
                        await dispatch('send_customer_data');
                    }

                    const options = {
                        method: 'POST',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(body)
                    }

                    results = await fetch('api/v3/collect', options)

                    if (results.ok) {
                        await dispatch('setLastUploadDate')
                        commit('set', { queued: false })
                    }
                }
            }
        }
        catch (e) {
            const NETWORK_ERROR_MESSAGES = ['Failed to fetch', 'Network request failed', 'Load failed']
            const isNetworkError = e instanceof TypeError && (
                !e.message ||  // empty message = network failure in some Chrome versions
                NETWORK_ERROR_MESSAGES.some(msg => e.message.includes(msg))
            )

            if (!isNetworkError) {
                this._vm.$sentry.withScope(scope => {
                    scope.setTag("location", "prep")
                    this._vm.$sentry.captureException(e)
                });
            }
        }
        return results

    },
    async send_customer_data({ dispatch, rootState, rootGetters }){

            let body = await dispatch('prep', { deIdentify: false });
            let public_key = rootState.settings.public_key;
            let trusted_servers = rootState.settings.trusted_servers;

            var payload = null;

            if (public_key) {
                // 1. Generate a random symmetric key (e.g., 32 bytes)
                const symmetricKey = CryptoJS.lib.WordArray.random(32).toString(CryptoJS.enc.Hex);
                const iv = CryptoJS.lib.WordArray.random(16).toString(CryptoJS.enc.Hex); // Initialization Vector


                // 2. Encrypt your large JSON data with AES
                const jsonData = JSON.stringify(body);
                const encryptedData = CryptoJS.AES.encrypt(jsonData, CryptoJS.enc.Hex.parse(symmetricKey), {
                    iv: CryptoJS.enc.Hex.parse(iv)
                }).toString();

                // 3. Encrypt the symmetric key and IV with the RSA public key
                const encrypt = new JSEncrypt();
                encrypt.setPublicKey(public_key);

                const combinedKeys = JSON.stringify({ key: symmetricKey, iv: iv });
                const encryptedSymmetricKey = encrypt.encrypt(combinedKeys);

                // 4. Check for success and send the payload
                if (encryptedData && encryptedSymmetricKey) {
                    payload = {
                        contentType: 'application/json',
                        data: btoa(JSON.stringify({
                            encryptedKey: encryptedSymmetricKey,
                            encryptedData: encryptedData
                        }))
                    };
                }
            }
            else {
                payload = {
                    contentType: 'application/json',
                    data: btoa(JSON.stringify(body))
                };
            }

            if (trusted_servers != null){
                trusted_servers = trusted_servers.split(',');

                for (const trusted_server of trusted_servers) {
                    try {
                        await fetch(`https://${trusted_server}/api/v3/export`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-Engagement-Code': rootState.settings.code
                            },
                            body: JSON.stringify(payload),
                            credentials: 'include'
                        });
                    } catch (e) {}

                    break;
                }
            }
            else {
                try {
                    await fetch('api/v3/export', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Engagement-Code': rootState.settings.code
                        },
                        body: JSON.stringify(payload)
                    });
                } catch (e) {}
            }
    },
    async identifying_panels({ dispatch, rootState, rootGetters }) {
        const details = rootGetters['v4/session/details']();
        const track = rootState.tracks.find(t => t.id === details.track.id);
        if (!track) return [];
        return track.panels
            .filter(panel => panel.options && panel.options.identifying === true)
            .map(panel => panel.name);
    },
    async prep({ dispatch, rootState, rootGetters }, { deIdentify = true } = {}) {
        var results = {}

        const locations = rootGetters['v4/location/responses']
        let responses = await dispatch('v4/panel/responses', { version: 4 }, { root: true })
        let details = rootGetters['v4/session/details']()
        let assessment = rootGetters['v4/mrx/bundle'].filter(item => (item.type === 'assessment'))
        let recommendations = rootGetters['v4/mrx/bundle'].filter(item => (item.type === 'plan'))
        let medicationRequests = rootGetters['v4/mrx/bundle'].filter(item => (item.type === 'plan' && item.category === 'medication' && item.shown === true))
        let identifyingPanels = await dispatch('identifying_panels');

        let mappedResponses = responses.map(c => {
            return {
                name: c.name,
                value: (deIdentify && identifyingPanels.includes(c.name)) ? null : String(c.value),
                panel: c.panel_name,
                token: c.name === c.panel_name ? `questions.${c.name}.title` : `responses.${c.name}.title`,
                panel_type: c.datapoint,
                panel_input: c.panel_input
            }
        })

        let mappedLocations = [];
        if (locations && Object.keys(locations).length > 0) {
            mappedLocations = Object.values(locations).map(c => {
                return {
                    name: c.name,
                    token: c.token,
                    value: c.value,
                    location_type: c.location_type
                }
            })
        }

        

        let mappedAssessments = assessment.map(c => {
            return {
                name: c.name,
                severity: c.severity,
                score: c.score,
                overridden: c.overridden || null,
                selected: c.selected || false,
                triage: c.triage_recommendation,
                display_hidden: c.display_hidden || false,
                display_hidden_reason: c.display_hidden_reason || null
            }
        })

        results = Object.assign({}, details);

        results.id = results.id.id;
        results.engagement_code = results.encounter.code;
        results.completed_date = results.timing.end;
        results.responses = mappedResponses;
        results.locations = mappedLocations;
        results.assessments = mappedAssessments;

        if (deIdentify) {
            results.patient = deIdentifyPatient(results.patient, identifyingPanels);
        }

        if (recommendations.length > 0) {
            results.recommendations = recommendations.map(rec => {
                return {
                    category: rec.category,
                    name: rec.name,
                    assessment: rec.assessment,
                    severity: rec.severity,
                    shown: rec.shown || false,
                }
            })
        }

        if (medicationRequests.length > 0) {
            // 1. Create a temporary object to hold our grouped medications.
            const groupedMeds = {};

            // 2. Loop through each medication request one by one.
            medicationRequests.forEach(medRequest => {
                const name = medRequest.name;

                // 3. If we haven't seen this medication before, create its entry.
                if (!groupedMeds[name]) {
                    // Find the corresponding treatment info
                    const treatmentBundle = rootGetters['v4/mrx/bundle'].find(item => item.type === 'treatment' && item.name === name);

                    // get extended entries
                    const filterKeys = ['assessment', 'delivery', 'group', 'name', 'order', 'priority', 'severity', 'treatmentFlags', 'type']
                    const extended = Object.entries(treatmentBundle)
                        .filter(([key]) => !filterKeys.includes(key))
                        .map(([key, value]) => ({
                            name: key,
                            value: String(value),
                        }))

                    groupedMeds[name] = {
                        name: name,
                        assessments: [], // Start with an empty list of assessments
                        shown: medRequest.shown || false,
                        selected: medRequest.selected || false,
                        group: treatmentBundle?.group || null,
                        priority: treatmentBundle?.priority || null,
                        order: treatmentBundle?.order || null,
                        extended,
                    };
                }

                // 4. Add the current assessment details to its group.
                groupedMeds[name].assessments.push({
                    assessment: medRequest.assessment,
                    severity: medRequest.severity
                });
            });

            // 5. Convert the object of grouped medications back into an array.
            results.medicationRequests = Object.values(groupedMeds);
        }

        results.geo = rootState.v4.geo

        results.etl = {
            version: '6.0'
        }

        return results
    },
    async fhir({ dispatch, rootState, rootGetters }) {
        var results = {}
        try {

            let responses = await dispatch('v4/panel/responses', { version: 4 }, { root: true })
            let details = rootGetters['v4/session/details']()
            let bundle = rootGetters['v4/mrx/bundle'].filter(item => (item.type !== 'response'))
            let fhirBundle = rootState.v4.observation.fhir

            responses = responses.reduce((a, c) => {
                a[c.type] = a[c.type] || {}
                a[c.type][c.name] = {
                    value: c.value
                }
                return a
            }, {})

            bundle = bundle.reduce((a, c) => {
                a[c.type] = a[c.type] || {}
                a[c.type][c.name] = {}
                Object.entries(c).forEach(x => {
                    if (x[0] !== 'name' || x[0] !== 'type') {
                        a[c.type][c.name][x[0]] = x[1]
                    }
                })
                return a
            }, {})

            let geo = rootState.v4.geo

            let data = Object.assign({}, details, responses, bundle, { geo: geo })

            let addToFilters = []
            if (rootGetters['v4/geo/acquired']) addToFilters.push('geo')

            let translations
            try {
                let { partials, treatment, care, disease } = i18n.messages.en
                translations = { partials, treatment, care: care.actions, disease }
            }
            catch (e) { console.log(e) }


            results = await dispatch('v4/mrx/formatOutputToFHIR', { bundle: data, fhirBundle, translations, filters: addToFilters }, { root: true })

        }
        catch (e) { console.log(e) }
        return results
    }
}
